import { CircleAlert } from 'lucide-react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@ophelos/ui/components/badge';
import { Alert, AlertDescription } from '@ophelos/ui/components/alert';

interface PartialPaymentSummaryProps {
  paying: number;
  owed: number;
  currency: string;
}

/** What a part payment does to the balance: a bar, the two figures, and a note that the rest is still owed. */
export function PartialPaymentSummary({ paying, owed, currency }: PartialPaymentSummaryProps) {
  const { t } = useTranslation();
  const share = owed > 0 ? paying / owed : 0;
  return (
    <section className="rounded-xl bg-card border px-5 py-4 stack gap-3" data-testid="partial-summary" data-total={owed} data-paying={paying}>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <span className="stack gap-0.5">
          <span className="text-muted-foreground">{t('paymentTabs.payingNow')}</span>
          <CurrencyDisplay amount={paying} currency={currency} className="text-sm font-semibold" />
        </span>
        <span className="stack gap-0.5 items-end text-right">
          <span className="text-muted-foreground">{t('paymentPlan.remainingBalance')}</span>
          <CurrencyDisplay amount={Math.max(0, owed - paying)} currency={currency} className="text-sm font-semibold" />
        </span>
      </div>

      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, share * 100)}%` }} />
      </div>
      <Badge variant="subtle">
        {Math.round(share * 100)}% {t('paymentTabs.ofBalance')}
      </Badge>

      {/* Withheld only once nothing is owed after it */}
      {paying < owed && (
        <Alert variant="warning">
          <CircleAlert className="size-3.5 shrink-0 mt-0.5" />
          <AlertDescription>{t('paymentTabs.willContactRemaining')}</AlertDescription>
        </Alert>
      )}
    </section>
  );
}
